import { useCallback, useEffect, useRef } from "react";

import { useAppSelector } from "app/hooks";
import {
  selectAuthScheme,
  selectCurrentUserToken
} from "features/auth/auth-slice";
import {
  AUDIO_CHANNEL_COUNT,
  AUDIO_SAMPLE_RATE,
  WS_CONNECTION_TIMEOUT_MS
} from "features/medical-scribe/streaming/constants";
import { getDynamicWsUrl } from "utils/api-config.util";
import { buildWebsocketAuthToken } from "utils/auth-header.util";

import { SPEECH_TO_TEXT_SILENCE_DETECTION_SETTINGS } from "../word-processor-value.utils";

type UseSpeechToTextStreamingProps = {
  isRecording: boolean;
  onAppendText: (text: string) => void;
  enabled?: boolean;
  onPauseDueToSilence?: () => void;
  onSilenceDetected?: () => void;
  microphoneDeviceId?: string;
  onStartFailed?: (error: unknown) => void;
};

type UseSpeechToTextStreamingReturn = {
  sendContinueRecording: () => void;
};

export const getSpeechToTextWebSocketUrl = (): string => {
  const base = getDynamicWsUrl();
  const normalized = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${normalized}/speech-to-text/`;
};

/**
 * AudioWorklet processor code as inline string.
 * This avoids CORS/CSP issues when loading worklet modules in production.
 * The code converts Float32 audio samples to Int16 PCM format and resamples
 * from the native sample rate to the target sample rate (16000 Hz).
 *
 * Firefox requires the AudioContext sample rate to match the stream's sample rate,
 * so we handle resampling in the worklet.
 */
const createAudioProcessorCode = (
  nativeSampleRate: number,
  targetSampleRate: number
): string => `
class AudioProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    this.nativeSampleRate = ${nativeSampleRate};
    this.targetSampleRate = ${targetSampleRate};
    this.resampleRatio = this.targetSampleRate / this.nativeSampleRate;

    // Ring buffer for resampling (pre-allocated to avoid GC)
    this.ringBufferSize = 2048;
    this.ringBuffer = new Float32Array(this.ringBufferSize);
    this.ringBufferWritePos = 0;
    this.ringBufferCount = 0;
    this.fractionalOffset = 0;

    // Pre-allocated output buffer
    this.outputBuffer = new Float32Array(256);
  }

  writeToRingBuffer(samples, length) {
    for (let i = 0; i < length; i++) {
      this.ringBuffer[this.ringBufferWritePos] = samples[i];
      this.ringBufferWritePos = (this.ringBufferWritePos + 1) % this.ringBufferSize;
      if (this.ringBufferCount < this.ringBufferSize) {
        this.ringBufferCount++;
      }
    }
  }

  readFromRingBufferAt(absoluteIndex) {
    const startPos = (this.ringBufferWritePos - this.ringBufferCount + this.ringBufferSize) % this.ringBufferSize;
    const pos = (startPos + absoluteIndex) % this.ringBufferSize;
    return this.ringBuffer[pos];
  }

  consumeFromRingBuffer(count) {
    this.ringBufferCount -= count;
  }

  process(inputs, outputs, parameters) {
    const input = inputs[0];
    if (input && input.length > 0) {
      const inputData = input[0];

      // If sample rates match, no resampling needed
      if (this.nativeSampleRate === this.targetSampleRate) {
        const pcmData = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        this.port.postMessage({ pcmData: pcmData.buffer }, [pcmData.buffer]);
      } else {
        // Write input samples to ring buffer
        this.writeToRingBuffer(inputData, inputData.length);

        const step = 1 / this.resampleRatio;
        let outputCount = 0;

        // Generate output samples with linear interpolation
        while (this.fractionalOffset < this.ringBufferCount - 1) {
          const srcIndexFloor = Math.floor(this.fractionalOffset);
          const srcIndexCeil = srcIndexFloor + 1;
          const fraction = this.fractionalOffset - srcIndexFloor;

          const s0 = this.readFromRingBufferAt(srcIndexFloor);
          const s1 = this.readFromRingBufferAt(srcIndexCeil);
          const sample = s0 * (1 - fraction) + s1 * fraction;

          const s = Math.max(-1, Math.min(1, sample));
          this.outputBuffer[outputCount++] = s < 0 ? s * 0x8000 : s * 0x7fff;

          this.fractionalOffset += step;
        }

        // Consume processed samples
        const samplesConsumed = Math.floor(this.fractionalOffset);
        if (samplesConsumed > 0) {
          this.consumeFromRingBuffer(samplesConsumed);
          this.fractionalOffset -= samplesConsumed;
        }

        // Send output if we produced any samples
        if (outputCount > 0) {
          const pcmData = new Int16Array(outputCount);
          for (let i = 0; i < outputCount; i++) {
            pcmData[i] = this.outputBuffer[i];
          }
          this.port.postMessage({ pcmData: pcmData.buffer }, [pcmData.buffer]);
        }
      }
    }

    return true;
  }
}

registerProcessor("audio-processor", AudioProcessor);
`;

/**
 * Create a blob URL for the AudioWorklet processor.
 */
const createAudioProcessorBlobUrl = (
  nativeSampleRate: number,
  targetSampleRate: number
): string => {
  const blob = new Blob(
    [createAudioProcessorCode(nativeSampleRate, targetSampleRate)],
    {
      type: "application/javascript"
    }
  );
  return URL.createObjectURL(blob);
};

/**
 * Normalize transcription text by fixing spacing around punctuation
 */
const normalizeTranscriptionText = (text: string): string => {
  return text
    .replaceAll(" .", ".")
    .replaceAll(" ,", ",")
    .replaceAll(" ?", "?")
    .replaceAll(" !", "!");
};

export default function useSpeechToTextStreaming({
  isRecording,
  onAppendText,
  enabled = true,
  onPauseDueToSilence,
  onSilenceDetected,
  microphoneDeviceId,
  onStartFailed
}: UseSpeechToTextStreamingProps): UseSpeechToTextStreamingReturn {
  const token = useAppSelector(selectCurrentUserToken);
  // Switched (Bearer) sessions must send "Bearer <jwt>" so the speech-to-text
  // consumer routes them to JWT auth instead of DRF-token lookup.
  const authScheme = useAppSelector(selectAuthScheme);

  // WebSocket refs
  const wsRef = useRef<WebSocket | null>(null);
  const authSentRef = useRef<boolean>(false);
  const authSucceededRef = useRef<boolean>(false);

  // Audio refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<AudioWorkletNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // State refs
  const isConnectingRef = useRef<boolean>(false);
  const isRecordingRef = useRef<boolean>(false);
  const isStoppingRef = useRef<boolean>(false);
  const onAppendTextRef = useRef(onAppendText);
  const onPauseDueToSilenceRef = useRef(onPauseDueToSilence);
  const onSilenceDetectedRef = useRef(onSilenceDetected);
  const microphoneDeviceIdRef = useRef(microphoneDeviceId);
  const onStartFailedRef = useRef(onStartFailed);
  const cleanupResourcesRef = useRef<(() => Promise<void>) | null>(null);

  // Keep callback refs up to date
  useEffect(() => {
    onAppendTextRef.current = onAppendText;
  }, [onAppendText]);

  useEffect(() => {
    onPauseDueToSilenceRef.current = onPauseDueToSilence;
  }, [onPauseDueToSilence]);

  useEffect(() => {
    onSilenceDetectedRef.current = onSilenceDetected;
  }, [onSilenceDetected]);

  useEffect(() => {
    microphoneDeviceIdRef.current = microphoneDeviceId;
  }, [microphoneDeviceId]);

  useEffect(() => {
    onStartFailedRef.current = onStartFailed;
  }, [onStartFailed]);

  // Keep isRecording ref in sync
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  /**
   * Ensure existing WebSocket is closed before creating new one
   */
  const ensureWebSocketClosed = useCallback(async (): Promise<void> => {
    const existing = wsRef.current;
    if (!existing) {
      return;
    }

    if (existing.readyState === WebSocket.CLOSED) {
      wsRef.current = null;
      return;
    }

    await new Promise<void>((resolve) => {
      let settled = false;
      const finalize = () => {
        if (settled) {
          return;
        }
        settled = true;
        existing.removeEventListener("close", handleClose);
        resolve();
      };

      const timeoutId = globalThis.setTimeout(() => {
        finalize();
      }, 1500);

      const handleClose = () => {
        globalThis.clearTimeout(timeoutId);
        finalize();
      };

      existing.addEventListener("close", handleClose);
      try {
        existing.close();
      } catch (err) {
        globalThis.clearTimeout(timeoutId);
        finalize();
      }
    });

    if (wsRef.current === existing) {
      wsRef.current = null;
    }
  }, []);

  /**
   * Create WebSocket connection with timeout and handlers
   */
  const createWebSocketConnection = useCallback((): Promise<WebSocket> => {
    return new Promise((resolve, reject) => {
      const wsUrl = getSpeechToTextWebSocketUrl();
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      let settled = false;

      // Reset auth flags for new connection
      authSentRef.current = false;
      authSucceededRef.current = false;

      const timeoutId = globalThis.setTimeout(() => {
        if (!settled) {
          settled = true;
          if (wsRef.current === ws) {
            wsRef.current = null;
          }
          try {
            ws.close(4000, "Connection timeout");
          } catch (err) {
            // Ignore
          }
          reject(new Error("Timed out establishing WebSocket connection"));
        }
      }, WS_CONNECTION_TIMEOUT_MS);

      const clearConnectionTimeout = () => {
        globalThis.clearTimeout(timeoutId);
      };

      ws.onopen = () => {
        clearConnectionTimeout();
        settled = true;

        // Send authentication immediately upon connection if token is available
        if (token && ws.readyState === WebSocket.OPEN) {
          try {
            const authMessage = {
              type: "authenticate",
              token: buildWebsocketAuthToken(token, authScheme)
            };

            ws.send(JSON.stringify(authMessage));
            authSentRef.current = true;
          } catch (err) {
            // Ignore - let normal flow handle it
          }
        }

        resolve(ws);
      };

      ws.onerror = (error) => {
        clearConnectionTimeout();
        if (!settled) {
          if (wsRef.current === ws) {
            wsRef.current = null;
          }
          reject(error);
        }
      };

      ws.onclose = (_event) => {
        clearConnectionTimeout();
        if (!settled && wsRef.current === ws) {
          wsRef.current = null;
        }
      };

      // Handle messages from backend
      // Store handler in a variable so we can remove it later
      const messageHandler = (event: MessageEvent) => {
        // Fast path: ignore binary data immediately
        if (event.data instanceof ArrayBuffer || event.data instanceof Blob) {
          return;
        }

        const rawData: string =
          typeof event.data === "string" ? event.data : String(event.data);

        if (rawData.length === 0 || !rawData.startsWith("{")) {
          // Not JSON or empty - skip processing
          return;
        }

        let msg: any;
        try {
          msg = JSON.parse(rawData);
        } catch {
          // Invalid JSON - skip
          return;
        }

        const msgType = msg?.type;
        switch (msgType) {
          case "authenticated":
            authSucceededRef.current = true;
            break;

          case "recording_started":
          case "recording_ended":
            // No action needed, just acknowledge
            break;

          case "transcribed_text": {
            // Most common case - optimize for performance
            const transcriptText = msg.text;
            if (!transcriptText || typeof transcriptText !== "string") {
              break;
            }

            // Single trim operation, reuse result
            const trimmedText = transcriptText.trim();
            if (trimmedText.length === 0) {
              break;
            }

            // Normalize and append
            const normalizedText = normalizeTranscriptionText(trimmedText);
            const textToAppend = normalizedText + " ";
            onAppendTextRef.current(textToAppend);
            break;
          }

          case "transcription_error":
            // Error handled by backend
            break;

          case "transcription_reconnected":
            // No action needed
            break;

          case "pause_due_to_silence":
            if (isRecordingRef.current && onPauseDueToSilenceRef.current) {
              onPauseDueToSilenceRef.current();
            }
            break;

          case "silence_detected":
            if (isRecordingRef.current && onSilenceDetectedRef.current) {
              onSilenceDetectedRef.current();
            }
            break;

          default:
            // Unknown message type - ignore
            break;
        }
      };

      ws.onmessage = messageHandler;
    });
  }, [token, authScheme]);

  /**
   * Authenticate with backend
   */
  const authenticate = useCallback(
    async (ws: WebSocket): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (ws.readyState !== WebSocket.OPEN) {
          reject(new Error("WebSocket is not open. Cannot authenticate."));
          return;
        }

        if (!token) {
          reject(
            new Error(
              "Authentication token is required. Please ensure you are logged in."
            )
          );
          return;
        }

        // Check if authentication already succeeded
        if (authSucceededRef.current && authSentRef.current) {
          resolve();
          return;
        }

        let resolved = false;
        let cleaned = false;

        const cleanup = () => {
          if (cleaned) return;
          cleaned = true;
          clearTimeout(timeout);
          if (originalOnMessage) {
            ws.onmessage = originalOnMessage;
          }
          if (originalOnClose) {
            ws.onclose = originalOnClose;
          }
        };

        const originalOnMessage = ws.onmessage;
        const originalOnClose = ws.onclose;

        const timeout = setTimeout(() => {
          cleanup();
          if (!resolved) {
            resolved = true;
            reject(
              new Error("Authentication timeout - no response from server")
            );
          }
        }, 5000);

        ws.onclose = (event) => {
          if (!resolved) {
            cleanup();
            resolved = true;
            reject(
              new Error(
                `Connection closed during authentication (code: ${event.code}, reason: ${event.reason || "Unknown"})`
              )
            );
          }
          if (originalOnClose) {
            originalOnClose.call(ws, event);
          }
        };

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);

            if (msg.type === "authenticated") {
              if (!resolved) {
                cleanup();
                resolved = true;
                authSucceededRef.current = true;
                resolve();
              }
            } else if (msg.error && msg.type !== "Results") {
              if (!resolved) {
                cleanup();
                resolved = true;
                reject(new Error(msg.error || "Authentication failed"));
              }
            } else if (originalOnMessage) {
              originalOnMessage.call(ws, event);
            }
          } catch (err) {
            if (originalOnMessage) {
              originalOnMessage.call(ws, event);
            }
          }
        };

        try {
          // Check if authentication was already sent (e.g., in onopen handler)
          if (authSentRef.current) {
            // Don't send again, just wait for the response
          } else {
            const authMessage = {
              type: "authenticate",
              token: buildWebsocketAuthToken(token, authScheme)
            };

            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify(authMessage));
              authSentRef.current = true;
            } else {
              throw new Error(`WebSocket not open (state: ${ws.readyState})`);
            }
          }
        } catch (err) {
          cleanup();
          if (!resolved) {
            resolved = true;
            reject(
              new Error(
                `Failed to send authentication message: ${
                  err instanceof Error ? err.message : "Unknown error"
                }`
              )
            );
          }
        }
      });
    },
    [token, authScheme]
  );

  /**
   * Setup audio pipeline for recording
   */
  const setupAudioPipeline = useCallback(
    async (stream: MediaStream): Promise<void> => {
      let audioContext = audioContextRef.current;
      let processor = processorRef.current;
      let source = sourceRef.current;
      const audioContextClosed = audioContext?.state === "closed";

      if (!audioContext || audioContextClosed || !processor || !source) {
        const AudioContextClass =
          globalThis.AudioContext ||
          (
            globalThis as typeof globalThis & {
              webkitAudioContext?: typeof AudioContext;
            }
          ).webkitAudioContext;
        if (!AudioContextClass) {
          throw new Error("AudioContext not supported in this browser");
        }

        if (!stream) {
          throw new Error("Audio stream is unavailable");
        }

        // Get the native sample rate from the audio track
        // Firefox requires the AudioContext sample rate to match the stream's sample rate
        const audioTrack = stream.getAudioTracks()[0];
        const trackSettings = audioTrack?.getSettings();
        const nativeSampleRate = trackSettings?.sampleRate || 48000;

        // Create AudioContext with the native sample rate to avoid Firefox compatibility issues
        // The AudioWorklet will handle resampling to the target rate (16000 Hz)
        audioContext = new AudioContextClass({
          sampleRate: nativeSampleRate
        });
        audioContextRef.current = audioContext;

        // Use blob URL to avoid CORS/CSP issues in production
        // Pass both sample rates so the worklet can resample if needed
        const processorBlobUrl = createAudioProcessorBlobUrl(
          nativeSampleRate,
          AUDIO_SAMPLE_RATE
        );
        try {
          await audioContext.audioWorklet.addModule(processorBlobUrl);
        } finally {
          // Clean up blob URL after module is loaded
          URL.revokeObjectURL(processorBlobUrl);
        }

        source = audioContext.createMediaStreamSource(stream);
        sourceRef.current = source;

        processor = new AudioWorkletNode(audioContext, "audio-processor");
        processorRef.current = processor;

        source.connect(processor);
      } else if (audioContext.state === "suspended") {
        await audioContext.resume();
      } else {
        // Reusing existing pipeline - ensure connection is maintained
        if (source && processor) {
          try {
            // Try to connect (will throw if already connected, which is fine)
            source.connect(processor);
          } catch (err: any) {
            // If already connected, that's fine - ignore error
          }
        }
      }

      if (!processor) {
        throw new Error("Audio processor failed to initialize");
      }

      // Handle messages from the AudioWorklet processor
      // Remove any existing handler first to prevent memory leaks
      processor.port.onmessage = null;

      processor.port.onmessage = (event) => {
        if (!event.data.pcmData) {
          return;
        }

        if (!isRecordingRef.current) {
          return;
        }

        const currentWs = wsRef.current;

        // Send to WebSocket for real-time transcription
        if (currentWs && currentWs.readyState === WebSocket.OPEN) {
          try {
            currentWs.send(event.data.pcmData);
          } catch (err) {
            // Don't throw - continue recording even if one packet fails
          }
        }
      };
    },
    []
  );

  /**
   * Get microphone stream
   */
  const getMicrophoneStream = useCallback(async (): Promise<MediaStream> => {
    let stream = streamRef.current;

    if (stream) {
      const hasEndedTrack = stream
        .getTracks()
        .some((track) => track.readyState === "ended");
      if (hasEndedTrack) {
        // Stop old tracks before getting new stream
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        stream = null;
      }
    }

    if (!stream) {
      const selectedMic = microphoneDeviceIdRef.current;
      const selectedDeviceConstraints: MediaStreamConstraints = {
        audio: {
          channelCount: AUDIO_CHANNEL_COUNT,
          deviceId: selectedMic ? { exact: selectedMic } : undefined
        }
      };

      // Note: We don't request a specific sample rate because browsers
      // (especially Firefox) may not honor it. Instead, we get the native
      // sample rate and resample in the AudioWorklet to the target rate.
      try {
        stream = await navigator.mediaDevices.getUserMedia(
          selectedDeviceConstraints
        );
      } catch (error) {
        // If the selected device is no longer available, fall back to default
        // microphone so speech-to-text continues to function.
        if (selectedMic) {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              channelCount: AUDIO_CHANNEL_COUNT
            }
          });
        } else {
          throw error;
        }
      }
      streamRef.current = stream;
    }

    return stream;
  }, []);

  /**
   * Cleanup all resources
   */
  const cleanupResources = useCallback(async () => {
    // Close WebSocket and remove message handler to prevent memory leaks
    if (wsRef.current) {
      try {
        // Remove message handler before closing
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.onclose = null;
        if (wsRef.current.readyState !== WebSocket.CLOSED) {
          wsRef.current.close(1000, "Cleanup");
        }
      } catch (err) {
        // Ignore cleanup errors
      }
      wsRef.current = null;
    }

    // Stop audio processor
    if (processorRef.current) {
      try {
        // Remove message handler first to prevent memory leaks
        processorRef.current.port.onmessage = null;
        processorRef.current.port.close();
        processorRef.current.disconnect();
      } catch (err) {
        // Ignore cleanup errors
      }
      processorRef.current = null;
    }

    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch (err) {
        // Ignore cleanup errors
      }
      sourceRef.current = null;
    }

    // Stop media stream tracks FIRST (must be done before closing audio context)
    // This ensures media stream is properly released before audio context closes
    if (streamRef.current) {
      try {
        for (const track of streamRef.current.getTracks()) {
          track.stop();
          track.enabled = false; // Additional safeguard
        }
      } catch (err) {
        // Ignore cleanup errors
      }
      streamRef.current = null;
    }

    // Close audio context (must be done after disconnecting nodes and stopping tracks)
    if (audioContextRef.current) {
      try {
        // Check state before closing to avoid errors
        if (audioContextRef.current.state !== "closed") {
          await audioContextRef.current.close();
        }
      } catch (err) {
        // Ignore cleanup errors
      }
      audioContextRef.current = null;
    }

    // Reset auth flags
    authSentRef.current = false;
    authSucceededRef.current = false;
    isConnectingRef.current = false;
    isStoppingRef.current = false;
  }, []);

  /**
   * Start recording
   */
  const startRecording = useCallback(async () => {
    // Check for token first
    if (!token) {
      onStartFailedRef.current?.(
        new Error(
          "Authentication token is required. Please ensure you are logged in."
        )
      );
      return;
    }

    // Guard against concurrent calls
    if (isConnectingRef.current) {
      return;
    }

    // Guard against stop in progress
    if (isStoppingRef.current) {
      return;
    }

    // Check if already recording - but only if we actually have an active connection
    // isRecordingRef.current might be true from the prop, but we need to check if we're actually connected
    const hasActiveConnection =
      wsRef.current && wsRef.current.readyState === WebSocket.OPEN;
    if (hasActiveConnection && !isConnectingRef.current) {
      return;
    }

    isConnectingRef.current = true;

    try {
      // Ensure any existing WebSocket is closed
      await ensureWebSocketClosed();

      // Double-check isRecording is still true (could have changed during async operations)
      if (!isRecordingRef.current) {
        isConnectingRef.current = false;
        return;
      }

      // Get microphone stream
      const stream = await getMicrophoneStream();

      // Double-check again after async operation
      if (!isRecordingRef.current) {
        // Cleanup stream if recording was cancelled
        stream.getTracks().forEach((track) => track.stop());
        isConnectingRef.current = false;
        return;
      }

      // Setup audio pipeline
      await setupAudioPipeline(stream);

      // Double-check again
      if (!isRecordingRef.current) {
        await cleanupResources();
        isConnectingRef.current = false;
        return;
      }

      // Connect WebSocket
      const ws = await createWebSocketConnection();

      // Double-check again
      if (!isRecordingRef.current) {
        await cleanupResources();
        isConnectingRef.current = false;
        return;
      }

      // Authenticate
      await authenticate(ws);

      // Final check before sending start message
      if (!isRecordingRef.current) {
        await cleanupResources();
        isConnectingRef.current = false;
        return;
      }

      // Send start_recording message
      const startMessage = {
        type: "start_recording",
        ...SPEECH_TO_TEXT_SILENCE_DETECTION_SETTINGS
      };
      ws.send(JSON.stringify(startMessage));

      isConnectingRef.current = false;
    } catch (error) {
      isConnectingRef.current = false;
      // Cleanup on error
      await cleanupResources();
      // Surface the failure so the caller can reset its recording UI. Without
      // this the toolbar mic stays lit while nothing is being transcribed.
      // Only report while the user still intends to be recording — a stop mid
      // handshake tears these resources down and is not a failure.
      if (isRecordingRef.current) {
        onStartFailedRef.current?.(error);
      }
    }
  }, [
    token,
    ensureWebSocketClosed,
    getMicrophoneStream,
    setupAudioPipeline,
    createWebSocketConnection,
    authenticate,
    cleanupResources
  ]);

  /**
   * Stop recording
   */
  const stopRecording = useCallback(async () => {
    // Guard against concurrent stop calls
    if (isStoppingRef.current) {
      return;
    }

    isStoppingRef.current = true;

    try {
      const ws = wsRef.current;

      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          // Send end_recording message
          const endMessage = {
            type: "end_recording"
          };
          ws.send(JSON.stringify(endMessage));
          // Give a small delay for the message to be sent before cleanup
          await new Promise((resolve) => setTimeout(resolve, 100));
        } catch (err) {
          // Ignore errors when stopping
        }
      }

      // Cleanup resources
      await cleanupResources();
    } finally {
      isStoppingRef.current = false;
    }
  }, [cleanupResources]);

  // Store cleanup function in ref for unmount cleanup
  useEffect(() => {
    cleanupResourcesRef.current = cleanupResources;
  }, [cleanupResources]);

  const sendContinueRecording = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        const continueMessage = {
          type: "continue_recording"
        };
        ws.send(JSON.stringify(continueMessage));
      } catch (err) {
        // Ignore errors when sending continue message
      }
    }
  }, []);

  // Effect to handle recording state changes
  useEffect(() => {
    // Early return if disabled - prevents any recording activity
    if (!enabled) {
      // Clean up any active resources when disabled
      // Use cleanupResources directly instead of stopRecording to avoid
      // sending end_recording messages when the hook is simply disabled
      void cleanupResources();
      return;
    }

    let isMounted = true;
    let isCancelled = false;

    const handleRecordingChange = async () => {
      // Check if cancelled before starting
      if (isCancelled || !isMounted) {
        return;
      }

      try {
        if (isRecording) {
          // Use the prop directly since that's what triggers the effect
          // The ref will be synced by the separate effect
          if (!isCancelled && isMounted) {
            await startRecording();
          }
        } else {
          // Check again before stopping
          if (!isCancelled && isMounted) {
            await stopRecording();
          }
        }
      } catch (error) {
        // Ignore errors in recording change handler
      }
    };

    handleRecordingChange();

    // Cleanup on unmount - always cleanup regardless of recording state
    return () => {
      isMounted = false;
      isCancelled = true;
      // Use ref to get latest cleanup function without dependency issues
      if (cleanupResourcesRef.current) {
        // Fire-and-forget cleanup on unmount (can't await in cleanup function)
        void cleanupResourcesRef.current();
      }
    };
  }, [enabled, isRecording, startRecording, stopRecording, cleanupResources]);

  return { sendContinueRecording };
}
